import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, RefreshCw, Layers, Plus, Trash2, Palette, Type, Link as LinkIcon, Hash, Coins } from "lucide-react";

const ManageToolsSection = () => {
  const [formData, setFormData] = useState({
    isActive: true,
    sectionSubtitle: "",
    sectionTitle: "",
    backgroundColor: "",
    browseAllLink: "",
    tools: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://allyoutube01-2d46d9aff565.herokuapp.com/api/tools-section");
      setFormData(res.data.data);
    } catch (error) {
      setMessage({ text: "Error loading data", type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleToolChange = (index, field, value) => {
    const updatedTools = [...formData.tools];
    updatedTools[index][field] = value;
    setFormData((prev) => ({ ...prev, tools: updatedTools }));
  };

  const addTool = () => {
    setFormData((prev) => ({
      ...prev,
      tools: [
        ...prev.tools,
        { 
          id: Date.now(), 
          title: "New Tool", 
          desc: "Tool description goes here...", 
          icon: "icofont-laptop", 
          color: "#ffffff", 
          link: "/",
          creditCost: 0, // NEW: Default credit cost for new tools
        },
      ],
    }));
  };

  const removeTool = (index) => {
    setFormData((prev) => ({
      ...prev,
      tools: prev.tools.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put("https://allyoutube01-2d46d9aff565.herokuapp.com/api/tools-section", formData);
      setMessage({ text: "Tools section updated successfully!", type: "success" });
    } catch (error) {
      setMessage({ text: "Failed to update section.", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset to default settings?")) {
      try {
        await axios.post("https://allyoutube01-2d46d9aff565.herokuapp.com/api/tools-section/reset");
        fetchData();
        setMessage({ text: "Reset to defaults.", type: "info" });
      } catch (error) {
        setMessage({ text: "Reset failed.", type: "danger" });
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading Admin Panel...</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold"><Layers className="me-2" size={28} /> Manage Tools Section</h2>
        <button className="btn btn-outline-secondary" onClick={handleReset}>
          <RefreshCw size={18} className="me-1" /> Reset Defaults
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card shadow-sm">
              <div className="card-header bg-dark fw-bold"><Type className="me-2" size={20} /> Section Settings</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-dark">Section Title</label>
                    <input type="text" className="form-control" name="sectionTitle" value={formData.sectionTitle} onChange={handleChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-dark">Section Subtitle</label>
                    <input type="text" className="form-control" name="sectionSubtitle" value={formData.sectionSubtitle} onChange={handleChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-dark">Background Color</label>
                    <div className="input-group">
                      <span className="input-group-text"><Palette size={18} /></span>
                      <input type="color" className="form-control form-control-color" name="backgroundColor" value={formData.backgroundColor} onChange={handleChange} title="Choose your color" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-dark">"Browse All Tools" Link Path</label>
                    <div className="input-group">
                      <span className="input-group-text"><Hash size={18} /></span>
                      <input type="text" className="form-control" name="browseAllLink" value={formData.browseAllLink} onChange={handleChange} placeholder="/user/tools" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-dark d-flex justify-content-between align-items-center fw-bold">
                <span><Layers className="me-2 text-white" size={20} /> SaaS Tools List</span>
                <button type="button" className="btn btn-sm btn-primary" onClick={addTool}>
                  <Plus size={16} className="me-1"/> Add Tool
                </button>
              </div>
              <div className="card-body">
                {formData.tools.map((tool, index) => (
                  <div key={tool.id || index} className="card bg-light mb-3 p-3 border-0">
                    <div className="d-flex justify-content-between mb-3">
                      <strong className="text-primary">Tool #{index + 1}</strong>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeTool(index)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    
                    {/* --- Row 1: Title, Description, Credit Cost --- */}
                    <div className="row g-3 mb-3">
                       <div className="col-md-4">
                        <label className="small text-muted">Title</label>
                        <input type="text" className="form-control" value={tool.title} onChange={(e) => handleToolChange(index, "title", e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="small text-muted">Description</label>
                        <input type="text" className="form-control" value={tool.desc} onChange={(e) => handleToolChange(index, "desc", e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="small text-muted"><Coins size={14} className="me-1"/> Credit Cost</label>
                        <div className="input-group">
                          <span className="input-group-text text-muted">$</span>
                          <input 
                            type="number" 
                            className="form-control" 
                            value={tool.creditCost} 
                            onChange={(e) => handleToolChange(index, "creditCost", e.target.value)} 
                          />
                        </div>
                      </div>
                    </div>

                    {/* --- Row 2: Icon, Color, Link --- */}
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="small text-muted">Icon Class (Icofont)</label>
                        <input type="text" className="form-control" value={tool.icon} onChange={(e) => handleToolChange(index, "icon", e.target.value)} placeholder="icofont-bulb-alt" />
                      </div>
                      <div className="col-md-3">
                        <label className="small text-muted">Brand Color</label>
                        <div className="input-group">
                           <input type="color" className="form-control form-control-color w-100" value={tool.color} onChange={(e) => handleToolChange(index, "color", e.target.value)} />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted">Link Path</label>
                        <div className="input-group">
                          <span className="input-group-text"><LinkIcon size={14} /></span>
                          <input type="text" className="form-control" value={tool.link} onChange={(e) => handleToolChange(index, "link", e.target.value)} placeholder="/tool-path" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {formData.tools.length === 0 && <p className="text-muted text-center">No tools added.</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button type="submit" className="btn btn-success px-4" disabled={saving}>
            {saving ? "Saving..." : <><Save size={18} className="me-2" /> Save Changes</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManageToolsSection;